import { makeAutoObservable } from "mobx";
import * as React from "react";
import { apiObjUrl } from "src/common/apiUrl";
import { ZAttrLabelsData } from "src/common/attributes/makeAttrLabel/types/ZAttrLabelsData";
import { formatAllLabels } from "src/common/attributes/makeAttrLabel/utils/formatAllLabels";
import { getAllLabelDataAttributes } from "src/common/attributes/makeAttrLabel/utils/getAllLabelDataAttributes";
import { findNodeByKey } from "src/common/findNodeByKey";
import { ifDef } from "src/common/ifDef";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { rest } from "src/common/rest";
import {
  FormBlockDef,
  FormBlockItem,
  FormWithBlockStore,
  blockItem,
} from "src/components/FormWithBlocks";
import { createEntityGroupBlock } from "src/pages/EntityCardPage/blockBuilder/buildMnemoGroup";
import { createItem2 } from "src/pages/EntityCardPage/blockBuilder/createItem2";
import { EntityInput } from "src/pages/EntityCardPage/fieldComponents/EntityInput";
import { IdLabel, getIdLabels } from "src/references/getIdNames";
import { makeDictNameById } from "src/types/AttrType";
import { ZObjectItem } from "src/types/ZObjectItem";
import { zPersonEntity } from "src/types/ZPersonEntity";
import { z } from "zod";
import { ZUid } from "src/types/zUid";
import { loadRoleGroups } from "../Obj2Tab/roles/rolesApi";
import { ZRolesGroup } from "../Obj2Tab/roles/roleTypes";
import { initTypeIcons } from "../Obj2Tab/utils/typeIcons";
import { apiLoadRoles } from "../RolesTab/apiRolesTab";
import { createPersonTree } from "./createPersonsTree";
import {
  PersonDetailsStore,
  zPersonEntityNoRoles,
} from "./PersonDetailsList/PersonDetailsStore";
import { CommonPersonNode } from "./PersonNode";

type PersonTabData = {
  groups: ZRolesGroup[];
  objects: ZObjectItem[];
};

const zPersonFieldValue = z.string().array().nullable().optional();

const zPersonFields = z.record(z.string(), zPersonFieldValue);
type ZPersonFields = z.infer<typeof zPersonFields>;

export class PersonsTabStore {
  constructor() {
    makeAutoObservable(this);
  }

  data: RemoteData<PersonTabData> = { status: "none" };

  setData(newData: RemoteData<PersonTabData>) {
    this.data = newData;
  }

  get treeData(): CommonPersonNode[] {
    return this.data.status === "ready"
      ? createPersonTree(this.data.result.groups)
      : [];
  }

  // Справочник атрибутов

  attrTypesList: IdLabel[] = [];

  setAttrTypesList(list: IdLabel[]) {
    this.attrTypesList = list;
  }

  labelsData: ZAttrLabelsData | null = null;

  setLabelsData(data: ZAttrLabelsData | null) {
    this.labelsData = data;
  }

  get attrTypesDict(): Record<number, string> {
    return makeDictNameById(this.attrTypesList);
  }

  async init() {
    try {
      this.setData({ status: "wait" });
      if (this.attrTypesList.length === 0) {
        this.setAttrTypesList(await getIdLabels("attrType", "attrType"));
        initTypeIcons(this.attrTypesDict);
      }

      const [groups, objects] = await Promise.all([
        loadRoleGroups(),
        apiLoadRoles(),
      ]);
      this.setData({ status: "ready", result: { groups, objects } });
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  currentPersonId: ZUid | "new" | null = null;

  setCurrentPersonId(persId: PersonsTabStore["currentPersonId"]) {
    this.currentPersonId = persId;
  }

  currentObjectId: ZUid = "";

  setCurrentObjectId(objectId: ZUid) {
    this.currentObjectId = objectId;
  }

  selectedKeys: React.Key[] = [];

  setSelectedKeys(keys: React.Key[]) {
    this.selectedKeys = keys;
    this.setCurrentPersonId(null);
  }

  get curNode(): CommonPersonNode | undefined {
    return ifDef(this.selectedKeys[0], (key) =>
      findNodeByKey(key, this.treeData),
    );
  }

  get personDetailsStore(): PersonDetailsStore | null {
    const { curNode } = this;
    if (!curNode || curNode.type !== "role" || this.data.status !== "ready")
      return null;
    // К сожалению, все необходимые данные приходится комбинировать из двух разных запросов
    const roleObject = this.data.result.objects.find(
      ({ roleId }) => roleId === curNode.role.roleId,
    );
    if (!roleObject) {
      onError(Error("Не найдено соответствующего объекта, описывающего роль"));
      return null;
    }
    return new PersonDetailsStore(curNode.role, roleObject);
  }

  personData: RemoteData<ZPersonFields> = { status: "none" };

  setPersonData(data: RemoteData<ZPersonFields>) {
    this.personData = data;
  }

  onNewPerson() {
    this.setCurrentPersonId("new");
    this.setPersonData({
      status: "ready",
      result: {
        login: null,
      },
    });
  }

  async onPerson(id: ZUid) {
    this.setCurrentPersonId(id);
    try {
      this.setPersonData({ status: "wait" });
      const resp = await rest.get(apiObjUrl(`/users/entity/${id}`));
      const entity = zPersonEntityNoRoles.parse(resp.data);
      const result: ZPersonFields = {
        login: [entity.userLogin],
      };
      entity.attributeValues.forEach(({ attributeId, values }) => {
        result[String(attributeId)] = values;
      });

      this.setLabelsData({
        labels: formatAllLabels(
          entity.labelData?.labels || [],
          getAllLabelDataAttributes(entity.labelData?.attributes),
          this.attrTypesDict,
        ),
        attributes: entity.labelData?.attributes,
      });

      this.setCurrentObjectId(String(entity.objectId));

      this.setPersonData({ status: "ready", result });
    } catch (error) {
      this.setPersonData({ status: "error", error });
    }
  }

  onCancelEdit() {
    this.setCurrentPersonId(null);
  }

  async onSubmit(formValues: unknown): Promise<ZPersonFields> {
    // Здесь не требуется try, т.к. обработка ошибок происходит в вызывающей функции
    const { currentPersonId, personDetailsStore } = this;
    if (!currentPersonId) {
      throw Error("FE. Не указан пользователь");
    }
    if (!personDetailsStore) {
      throw Error("FE. Отсутствует стор пользвателей");
    }
    const { login, ...fields } = zPersonFields.parse(formValues);
    const data = {
      objectId: personDetailsStore.roleObject.id,
      attributeValues: Object.entries(fields).map(([key, values]) => ({
        attributeId: key,
        values: values ?? [],
      })),
    };
    const params = { login: login?.[0] };
    if (currentPersonId === "new") {
      // Создание нового пользователя
      const resp = await rest.post(apiObjUrl("/users"), data, { params });
      const newPers = zPersonEntity.parse(resp.data);
      this.onPerson(newPers.id); // здесь не требуется wait, т.к. это уже работает асинхронно
    } else {
      // Обновление
      await rest.put(apiObjUrl(`/users/${currentPersonId}`), data);
    }
    return fields;
  }

  get initialData(): ZPersonFields {
    return this.personData.status === "ready" ? this.personData.result : {};
  }

  get formBlock(): FormBlockDef | undefined {
    const { personDetailsStore } = this;
    if (!personDetailsStore) return undefined;

    const buildCtx = {
      canUpdate: true,
      typesMap: this.attrTypesDict,

      labelsData: this.labelsData ?? undefined,

      objectId: this.currentObjectId,
    };
    const items: FormBlockItem[] = [
      // Специальное поле для логина
      blockItem(
        "login",
        "Логин",
        EntityInput,
        { allowClear: true, disabled: this.currentPersonId !== "new" },
        {
          rules: [
            { required: true },
            {
              pattern: /^.+@.+$/,
              message: "В качестве логина нужно указать email",
            },
          ],
        },
      ),
      // А дальше поля, генерируемые из атрибутов
      ...personDetailsStore.roleObject.attributes.map((a) =>
        createItem2(a, buildCtx),
      ),
    ];

    return createEntityGroupBlock("root", "", items, []);
  }

  formStore = new FormWithBlockStore();
}
